<template>
	<div class="flex flex-col gap-4">
		<!-- Query Controls -->
		<div class="sticky top-0 z-10 flex flex-col gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
			<div class="flex flex-row items-center justify-between gap-4 flex-wrap">
				<div class="flex flex-col gap-2">
					<div class="flex flex-row items-center gap-4">
						<span class="font-semibold text-sm text-gray-700">Query Mode:</span>
						<span class="flex items-center gap-2">
							<RadioButton v-model="queryMode" inputId="modeAll" value="all" @change="onLoad" />
							<label for="modeAll" class="text-sm cursor-pointer">All History</label>
						</span>
						<span class="flex items-center gap-2">
							<RadioButton v-model="queryMode" inputId="modeBetween" value="between" />
							<label for="modeBetween" class="text-sm cursor-pointer">Date Range</label>
						</span>
					</div>

					<div v-if="queryMode === 'between'" class="flex flex-row gap-4">
						<div class="flex flex-col gap-1">
							<label class="text-xs font-medium text-gray-600">From</label>
							<DatePicker v-model="fromDate" placeholder="Select from date" :showIcon="true" dateFormat="M dd, yy" @date-select="onLoad" />
						</div>
						<div class="flex flex-col gap-1">
							<label class="text-xs font-medium text-gray-600">To</label>
							<DatePicker v-model="toDate" placeholder="Select to date" :showIcon="true" dateFormat="M dd, yy" @date-select="onLoad" />
						</div>
					</div>
				</div>

			</div>
		</div>

		<!-- Loading indicator -->
		<div v-if="loading" class="flex items-center justify-center gap-2 py-8 text-gray-500">
			<i class="pi pi-spin pi-spinner text-xl"></i>
			<span>Loading history data...</span>
		</div>

		<!-- No results message -->
		<div v-else-if="hasQueried && !hasData" class="flex items-center justify-center gap-2 py-6 text-gray-500">
			<i class="pi pi-info-circle text-lg"></i>
			<span>No history records found for the selected criteria.</span>
		</div>

		<!-- Change event feed -->
		<ul v-else-if="hasData" class="flex flex-col gap-2">
			<li v-for="(event, idx) in changeEvents" :key="idx" class="flex min-h-16 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
				<!-- Meta column -->
				<div class="flex flex-col justify-center gap-1 px-3 py-0 w-44 shrink-0 border-r border-gray-200 my-2">
					<span class="text-xs text-gray-700">{{ event.label }}</span>
					<span class="text-xs font-semibold" :class="{ 'text-green-600': event.eventType === 'created', 'text-red-600': event.eventType === 'deleted', 'text-amber-500': event.eventType === 'updated' }">
						{{ event.eventType === "created" ? "Created" : event.eventType === "deleted" ? "Deleted" : "Updated" }}
					</span>
					<span class="text-xs text-gray-600">{{ event.personName }}</span>
					<span class="text-xs text-gray-400">{{ formatDateTime(event.timestamp) }}</span>
				</div>

				<!-- Fields / changes -->
				<div class="flex-1 px-4 py-3">
					<template v-if="event.eventType === 'created' || event.eventType === 'deleted'">
						<ul class="flex flex-col gap-1">
							<li
								v-for="f in event.fields.length > 5 && !expandedEvents.has(idx) ? event.fields.slice(0, 5) : event.fields"
								:key="f.field"
								class="flex items-baseline gap-2 text-sm"
							>
								<span class="w-40 shrink-0 text-xs text-gray-400">{{ formatFieldLabel(f.field) }}</span>
								<span class="text-gray-700">{{ formatValue(f.value) }}</span>
							</li>
						</ul>
					</template>

					<template v-else>
						<ul v-if="event.fields.length" class="flex flex-col gap-1 mb-2">
							<li
								v-for="f in event.fields.filter(f => f.alwaysShow)"
								:key="f.field"
								class="flex items-baseline gap-2 text-sm"
							>
								<span class="w-40 shrink-0 text-xs text-gray-400">{{ f.field }}</span>
								<span class="text-gray-500 text-xs">{{ formatValue(f.value) }}</span>
							</li>
						</ul>
						<ul class="flex flex-col gap-2">
							<li
								v-for="c in event.changes.length > 5 && !expandedEvents.has(idx) ? event.changes.slice(0, 5) : event.changes"
								:key="c.field"
								class="flex flex-col gap-1"
							>
								<span class="text-xs text-gray-400">{{ formatFieldLabel(c.field) }}</span>
								<div class="flex items-center gap-2 max-w-lg">
									<span class="px-2 py-0.5 rounded border border-red-200 bg-red-50 text-red-600 text-xs flex-1 min-w-0 truncate">{{
										formatValue(c.from)
									}}</span>
									<i class="pi pi-arrow-right text-gray-400 text-xs shrink-0"></i>
									<span class="px-2 py-0.5 rounded border border-green-200 bg-green-50 text-green-700 text-xs flex-1 min-w-0 truncate">{{
										formatValue(c.to)
									}}</span>
								</div>
							</li>
						</ul>
					</template>

					<button
						v-if="(event.eventType === 'updated' ? event.changes.length : event.fields.length) > 5"
						class="mt-1.5 text-xs text-blue-500 hover:text-blue-700 hover:underline"
						@click="toggleExpanded(idx)"
					>
						{{ expandedEvents.has(idx) ? "Show less" : `Show all ${event.eventType === "updated" ? event.changes.length : event.fields.length} fields` }}
					</button>
				</div>
			</li>
		</ul>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue"
import RadioButton from "primevue/radiobutton"
import DatePicker from "primevue/datepicker"
import { formatDateTime, formatFieldLabel, formatValue } from "@/composables/useTemporalDiff"
import type { ChangeEvent, QueryMode, TemporalLoadRequest } from "@/models/temporalTypes"

// ─── Props ────────────────────────────────────────────────────────────────────

const props = defineProps<{
	changeEvents: ChangeEvent[]
	loading: boolean
	hasQueried: boolean
}>()

const emit = defineEmits<{
	load: [request: TemporalLoadRequest]
	clear: []
}>()

// ─── Query controls state (owned by this component) ───────────────────────────

const queryMode = ref<QueryMode>("all")
const fromDate = ref<Date | null>(null)
const toDate = ref<Date | null>(null)
const expandedEvents = ref(new Set<number>())

// ─── Computed ─────────────────────────────────────────────────────────────────

const hasData = computed(() => props.changeEvents.length > 0)

// ─── Actions ──────────────────────────────────────────────────────────────────

function onLoad(): void {
	if (queryMode.value === "all") {
		emit("load", { mode: "all" })
	} else if (fromDate.value && toDate.value) {
		const from = new Date(fromDate.value)
		from.setHours(0, 0, 0, 0)
		const to = new Date(toDate.value)
		to.setHours(23, 59, 59, 999)
		emit("load", { mode: "between", from, to })
	}
}

function toggleExpanded(idx: number): void {
	const next = new Set(expandedEvents.value)
	next.has(idx) ? next.delete(idx) : next.add(idx)
	expandedEvents.value = next
}

watch(queryMode, (mode) => {
	if (mode === "all" || (mode === "between" && fromDate.value && toDate.value)) {
		onLoad()
	}
})

watch([fromDate, toDate], () => {
	if (queryMode.value === "between" && fromDate.value && toDate.value) {
		onLoad()
	}
})
</script>
